export type SoundType =
  | 'click'
  | 'dialogue'
  | 'correct'
  | 'wrong'
  | 'step'
  | 'boss'
  | 'revision'
  | 'theme'
  | 'stele'
  | 'victory';

export type BGMTrack = 'none' | 'prologue' | 'world_map' | 'in_game' | 'boss' | 'victory';

// Audio Context singleton
let audioCtx: AudioContext | null = null;
let masterGain: GainNode | null = null;
let musicGain: GainNode | null = null;
let ambientGain: GainNode | null = null;
let sfxGain: GainNode | null = null;

// Settings state
let isMuted = false;
let isMusicEnabled = true;
let isAmbienceEnabled = true;
let isRecordingActive = false;

// Active tracking
let currentTrack: BGMTrack = 'none';
let musicIntervalId: number | null = null;
let ambientWindNode: GainNode | null = null;
let ambientWindSource: AudioBufferSourceNode | null = null;
let ambientWindLfo: OscillatorNode | null = null;
let ambientCricketIntervalId: number | null = null;
let cachedNoiseBuffer: AudioBuffer | null = null;

// Listeners for React components
type AudioStateListener = () => void;
const listeners: Set<AudioStateListener> = new Set();

const notifyListeners = () => {
  listeners.forEach((cb) => {
    try {
      cb();
    } catch {}
  });
};

export const subscribeAudioState = (cb: AudioStateListener): (() => void) => {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
};

export const getAudioState = () => ({
  isMuted,
  isMusicEnabled,
  isAmbienceEnabled,
  isRecordingActive,
  currentTrack,
});

// Sound is enabled and unmuted by default
try {
  const savedMusic = localStorage.getItem('asirem_audio_music');
  if (savedMusic !== null) {
    isMusicEnabled = savedMusic === 'true';
  }
  const savedMute = localStorage.getItem('asirem_audio_muted');
  if (savedMute !== null) {
    isMuted = savedMute === 'true';
  }
  const savedAmbience = localStorage.getItem('asirem_audio_ambience');
  if (savedAmbience !== null) {
    isAmbienceEnabled = savedAmbience === 'true';
  }
} catch {}

// Ensure AudioContext is initialized safely with interactive latency
export const getAudioContext = (): AudioContext | null => {
  if (typeof window === 'undefined') return null;
  if (!audioCtx) {
    const AudioContextClass =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    if (AudioContextClass) {
      audioCtx = new AudioContextClass({ latencyHint: 'interactive' });

      // Master Gain
      masterGain = audioCtx.createGain();
      masterGain.gain.setValueAtTime(isMuted ? 0 : 0.8, audioCtx.currentTime);
      masterGain.connect(audioCtx.destination);

      // Sub gains
      musicGain = audioCtx.createGain();
      musicGain.gain.setValueAtTime(isMusicEnabled && !isRecordingActive ? 0.35 : 0, audioCtx.currentTime);
      musicGain.connect(masterGain);

      ambientGain = audioCtx.createGain();
      ambientGain.gain.setValueAtTime(isAmbienceEnabled && !isRecordingActive ? 0.25 : 0, audioCtx.currentTime);
      ambientGain.connect(masterGain);

      sfxGain = audioCtx.createGain();
      sfxGain.gain.setValueAtTime(0.75, audioCtx.currentTime);
      sfxGain.connect(masterGain);
    }
  }

  if (audioCtx && audioCtx.state === 'suspended') {
    audioCtx.resume().catch(() => {});
  }

  return audioCtx;
};

// Immediate unlock handler on first user touch/pointerdown
export const unlockAudioContext = () => {
  const ctx = getAudioContext();
  if (ctx && ctx.state === 'suspended') {
    ctx.resume().catch(() => {});
  }
};

// Silence all background music & ambience during voice recording
export const setRecordingActive = (active: boolean): void => {
  isRecordingActive = active;
  if (active) {
    // Immediately stop sequence and silence music & ambience
    stopMusicSequence();
    stopAmbience();
    if (musicGain && audioCtx) {
      musicGain.gain.cancelScheduledValues(audioCtx.currentTime);
      musicGain.gain.setValueAtTime(0, audioCtx.currentTime);
    }
    if (ambientGain && audioCtx) {
      ambientGain.gain.cancelScheduledValues(audioCtx.currentTime);
      ambientGain.gain.setValueAtTime(0, audioCtx.currentTime);
    }
  } else {
    // Restore gains
    if (musicGain && audioCtx) {
      musicGain.gain.cancelScheduledValues(audioCtx.currentTime);
      musicGain.gain.setValueAtTime(isMusicEnabled ? 0.35 : 0, audioCtx.currentTime);
    }
    if (ambientGain && audioCtx) {
      ambientGain.gain.cancelScheduledValues(audioCtx.currentTime);
      ambientGain.gain.setValueAtTime(isAmbienceEnabled ? 0.25 : 0, audioCtx.currentTime);
    }
    // Resume current track if music is enabled
    if (isMusicEnabled && currentTrack !== 'none') {
      playBGM(currentTrack);
    }
  }
  notifyListeners();
};

export const getIsRecordingActive = (): boolean => isRecordingActive;

// Toggle Master Mute
export const toggleMute = (): boolean => {
  isMuted = !isMuted;
  try {
    localStorage.setItem('asirem_audio_muted', String(isMuted));
  } catch {}
  if (masterGain && audioCtx) {
    masterGain.gain.cancelScheduledValues(audioCtx.currentTime);
    masterGain.gain.setValueAtTime(isMuted ? 0 : 0.8, audioCtx.currentTime);
  }
  notifyListeners();
  return isMuted;
};

// Toggle Music
export const toggleMusic = (): boolean => {
  isMusicEnabled = !isMusicEnabled;
  try {
    localStorage.setItem('asirem_audio_music', String(isMusicEnabled));
  } catch {}
  if (musicGain && audioCtx) {
    musicGain.gain.cancelScheduledValues(audioCtx.currentTime);
    musicGain.gain.setValueAtTime(isMusicEnabled && !isRecordingActive ? 0.35 : 0, audioCtx.currentTime);
  }
  if (!isMusicEnabled) {
    stopMusicSequence();
  } else if (currentTrack !== 'none' && !isRecordingActive) {
    playBGM(currentTrack);
  }
  notifyListeners();
  return isMusicEnabled;
};

// Set Music explicitly
export const setMusicEnabled = (enabled: boolean): boolean => {
  isMusicEnabled = enabled;
  try {
    localStorage.setItem('asirem_audio_music', String(isMusicEnabled));
  } catch {}
  if (musicGain && audioCtx) {
    musicGain.gain.cancelScheduledValues(audioCtx.currentTime);
    musicGain.gain.setValueAtTime(isMusicEnabled && !isRecordingActive ? 0.35 : 0, audioCtx.currentTime);
  }
  if (!isMusicEnabled) {
    stopMusicSequence();
  } else if (currentTrack !== 'none' && !isRecordingActive) {
    playBGM(currentTrack);
  }
  notifyListeners();
  return isMusicEnabled;
};

// Toggle Ambience
export const toggleAmbience = (): boolean => {
  isAmbienceEnabled = !isAmbienceEnabled;
  try {
    localStorage.setItem('asirem_audio_ambience', String(isAmbienceEnabled));
  } catch {}
  if (ambientGain && audioCtx) {
    ambientGain.gain.cancelScheduledValues(audioCtx.currentTime);
    ambientGain.gain.setValueAtTime(isAmbienceEnabled ? 0.25 : 0, audioCtx.currentTime);
  }
  if (!isAmbienceEnabled) {
    stopAmbience();
  } else if (currentTrack !== 'none') {
    startAmbienceForTrack(currentTrack);
  }
  notifyListeners();
  return isAmbienceEnabled;
};

// =========================================================================
// SFX ENGINE (Sound Effects) - Ultra-Lightweight & Immediate
// =========================================================================
export const playSound = (type: SoundType): void => {
  try {
    const ctx = getAudioContext();
    if (!ctx || !sfxGain || isMuted) return;

    if (ctx.state === 'suspended') {
      ctx.resume().catch(() => {});
    }

    const now = ctx.currentTime;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(sfxGain);

    const cleanup = () => {
      try {
        osc.disconnect();
        gain.disconnect();
      } catch {}
    };
    osc.onended = cleanup;

    switch (type) {
      case 'click':
        osc.type = 'sine';
        osc.frequency.setValueAtTime(750, now);
        osc.frequency.exponentialRampToValueAtTime(1100, now + 0.04);
        gain.gain.setValueAtTime(0.14, now);
        gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.04);
        osc.start(now);
        osc.stop(now + 0.04);
        break;

      case 'dialogue':
        osc.type = 'sine';
        osc.frequency.setValueAtTime(260 + Math.random() * 30, now);
        osc.frequency.exponentialRampToValueAtTime(150, now + 0.02);
        gain.gain.setValueAtTime(0.01, now);
        gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.02);
        osc.start(now);
        osc.stop(now + 0.02);
        break;

      case 'correct':
        osc.type = 'triangle';
        osc.frequency.setValueAtTime(523.25, now);
        osc.frequency.setValueAtTime(659.25, now + 0.06);
        osc.frequency.setValueAtTime(783.99, now + 0.12);
        osc.frequency.setValueAtTime(1046.5, now + 0.18);
        gain.gain.setValueAtTime(0.16, now);
        gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.32);
        osc.start(now);
        osc.stop(now + 0.32);
        break;

      case 'wrong':
        osc.type = 'sawtooth';
        osc.frequency.setValueAtTime(200, now);
        osc.frequency.setValueAtTime(120, now + 0.12);
        gain.gain.setValueAtTime(0.14, now);
        gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.25);
        osc.start(now);
        osc.stop(now + 0.25);
        break;

      case 'step':
        osc.type = 'sine';
        osc.frequency.setValueAtTime(160, now);
        osc.frequency.exponentialRampToValueAtTime(80, now + 0.06);
        gain.gain.setValueAtTime(0.07, now);
        gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.06);
        osc.start(now);
        osc.stop(now + 0.06);
        break;

      case 'boss':
        osc.type = 'sawtooth';
        osc.frequency.setValueAtTime(110, now);
        osc.frequency.setValueAtTime(55, now + 0.4);
        gain.gain.setValueAtTime(0.2, now);
        gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.7);
        osc.start(now);
        osc.stop(now + 0.7);
        break;

      case 'revision':
        osc.type = 'triangle';
        osc.frequency.setValueAtTime(320, now);
        osc.frequency.setValueAtTime(480, now + 0.15);
        gain.gain.setValueAtTime(0.12, now);
        gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.35);
        osc.start(now);
        osc.stop(now + 0.35);
        break;

      case 'theme':
        osc.type = 'sine';
        osc.frequency.setValueAtTime(659.25, now);
        osc.frequency.setValueAtTime(880.0, now + 0.1);
        gain.gain.setValueAtTime(0.14, now);
        gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.3);
        osc.start(now);
        osc.stop(now + 0.3);
        break;

      case 'stele': {
        cleanup();
        const chordOsc1 = ctx.createOscillator();
        const chordGain = ctx.createGain();
        chordOsc1.connect(chordGain);
        chordGain.connect(sfxGain);

        chordOsc1.type = 'triangle';
        chordOsc1.frequency.setValueAtTime(523.25, now);
        chordOsc1.frequency.setValueAtTime(659.25, now + 0.15);
        chordOsc1.frequency.setValueAtTime(783.99, now + 0.3);
        chordOsc1.frequency.setValueAtTime(1046.5, now + 0.45);

        chordGain.gain.setValueAtTime(0.18, now);
        chordGain.gain.exponentialRampToValueAtTime(0.0001, now + 1.2);

        chordOsc1.onended = () => {
          try {
            chordOsc1.disconnect();
            chordGain.disconnect();
          } catch {}
        };

        chordOsc1.start(now);
        chordOsc1.stop(now + 1.2);
        break;
      }

      case 'victory': {
        cleanup();
        const chordOsc1 = ctx.createOscillator();
        const chordGain = ctx.createGain();
        chordOsc1.connect(chordGain);
        chordGain.connect(sfxGain);

        chordOsc1.type = 'triangle';
        chordOsc1.frequency.setValueAtTime(523.25, now);
        chordOsc1.frequency.setValueAtTime(659.25, now + 0.12);
        chordOsc1.frequency.setValueAtTime(1046.5, now + 0.25);

        chordGain.gain.setValueAtTime(0.18, now);
        chordGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.9);

        chordOsc1.onended = () => {
          try {
            chordOsc1.disconnect();
            chordGain.disconnect();
          } catch {}
        };

        chordOsc1.start(now);
        chordOsc1.stop(now + 0.9);
        break;
      }
    }
  } catch {}
};

// =========================================================================
// AMBIENT SOUND GENERATOR (Procedural Wind & Nature)
// =========================================================================
const getOrCreateNoiseBuffer = (ctx: AudioContext): AudioBuffer => {
  if (cachedNoiseBuffer && cachedNoiseBuffer.sampleRate === ctx.sampleRate) {
    return cachedNoiseBuffer;
  }
  const bufferSize = ctx.sampleRate * 2;
  const noiseBuffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
  const output = noiseBuffer.getChannelData(0);
  let lastOut = 0.0;
  for (let i = 0; i < bufferSize; i++) {
    const white = Math.random() * 2 - 1;
    output[i] = (lastOut + 0.02 * white) / 1.02;
    lastOut = output[i];
    output[i] *= 3.5;
  }
  cachedNoiseBuffer = noiseBuffer;
  return noiseBuffer;
};

const startAmbienceForTrack = (track: BGMTrack) => {
  stopAmbience();
  if (!isAmbienceEnabled || track === 'none') return;

  const ctx = getAudioContext();
  if (!ctx || !ambientGain) return;

  try {
    const noiseBuffer = getOrCreateNoiseBuffer(ctx);
    const whiteNoise = ctx.createBufferSource();
    whiteNoise.buffer = noiseBuffer;
    whiteNoise.loop = true;

    const filter = ctx.createBiquadFilter();
    filter.type = 'bandpass';
    filter.frequency.setValueAtTime(track === 'prologue' ? 320 : 450, ctx.currentTime);
    filter.Q.setValueAtTime(2.5, ctx.currentTime);

    const lfo = ctx.createOscillator();
    const lfoGain = ctx.createGain();
    lfo.frequency.setValueAtTime(0.12, ctx.currentTime);
    lfoGain.gain.setValueAtTime(140, ctx.currentTime);
    lfo.connect(lfoGain);
    lfoGain.connect(filter.frequency);

    const windGain = ctx.createGain();
    const targetVol = track === 'prologue' ? 0.18 : 0.12;
    windGain.gain.setValueAtTime(targetVol, ctx.currentTime);

    whiteNoise.connect(filter);
    filter.connect(windGain);
    windGain.connect(ambientGain);

    whiteNoise.start(0);
    lfo.start(0);

    ambientWindSource = whiteNoise;
    ambientWindLfo = lfo;
    ambientWindNode = windGain;

    if (track === 'world_map' || track === 'prologue') {
      ambientCricketIntervalId = window.setInterval(() => {
        if (!isAmbienceEnabled || isMuted) return;
        playSoftBirdOrCricket(track === 'prologue' ? 'cricket' : 'bird');
      }, 5000 + Math.random() * 3000);
    }
  } catch {}
};

const playSoftBirdOrCricket = (type: 'bird' | 'cricket') => {
  const ctx = getAudioContext();
  if (!ctx || !ambientGain || isMuted) return;

  const now = ctx.currentTime;
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();

  osc.connect(gain);
  gain.connect(ambientGain);

  osc.onended = () => {
    try {
      osc.disconnect();
      gain.disconnect();
    } catch {}
  };

  if (type === 'cricket') {
    osc.type = 'sine';
    osc.frequency.setValueAtTime(4200, now);
    osc.frequency.setValueAtTime(4600, now + 0.04);
    osc.frequency.setValueAtTime(4200, now + 0.08);
    gain.gain.setValueAtTime(0.015, now);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.12);
    osc.start(now);
    osc.stop(now + 0.12);
  } else {
    osc.type = 'sine';
    osc.frequency.setValueAtTime(2400, now);
    osc.frequency.exponentialRampToValueAtTime(3200, now + 0.08);
    osc.frequency.exponentialRampToValueAtTime(2600, now + 0.18);
    gain.gain.setValueAtTime(0.02, now);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.22);
    osc.start(now);
    osc.stop(now + 0.22);
  }
};

const stopAmbience = () => {
  if (ambientWindSource) {
    try {
      ambientWindSource.stop();
      ambientWindSource.disconnect();
    } catch {}
    ambientWindSource = null;
  }
  if (ambientWindLfo) {
    try {
      ambientWindLfo.stop();
      ambientWindLfo.disconnect();
    } catch {}
    ambientWindLfo = null;
  }
  if (ambientWindNode) {
    try {
      ambientWindNode.disconnect();
    } catch {}
    ambientWindNode = null;
  }
  if (ambientCricketIntervalId !== null) {
    clearInterval(ambientCricketIntervalId);
    ambientCricketIntervalId = null;
  }
};

// =========================================================================
// PROCEDURAL KABYLE MUSIC GENERATOR (Lightweight & Safe)
// =========================================================================

const NOTES = {
  C3: 130.81,
  D3: 146.83,
  Eb3: 155.56,
  E3: 164.81,
  F3: 174.61,
  G3: 196.0,
  Ab3: 207.65,
  A3: 220.0,
  Bb3: 233.08,
  B3: 246.94,
  C4: 261.63,
  D4: 293.66,
  Eb4: 311.13,
  E4: 329.63,
  F4: 349.23,
  G4: 392.0,
  Ab4: 415.3,
  A4: 440.0,
  Bb4: 466.16,
  B4: 493.88,
  C5: 523.25,
  D5: 587.33,
  Eb5: 622.25,
  E5: 659.25,
  F5: 698.46,
  G5: 783.99,
  A5: 880.0,
  C6: 1046.5,
};

// Play a flute-like Gasba note
const playGasbaNote = (freq: number, duration: number, vol = 0.08) => {
  const ctx = getAudioContext();
  if (!ctx || !musicGain || isMuted || !isMusicEnabled || isRecordingActive) return;

  const now = ctx.currentTime;
  const osc = ctx.createOscillator();
  const vibrato = ctx.createOscillator();
  const vibratoGain = ctx.createGain();
  const gain = ctx.createGain();

  vibrato.frequency.setValueAtTime(5.2, now);
  vibratoGain.gain.setValueAtTime(4.5, now);
  vibrato.connect(vibratoGain);
  vibratoGain.connect(osc.frequency);

  osc.type = 'sine';
  osc.frequency.setValueAtTime(freq, now);

  gain.gain.setValueAtTime(0.001, now);
  gain.gain.linearRampToValueAtTime(vol, now + 0.1);
  gain.gain.setValueAtTime(vol * 0.9, now + duration - 0.12);
  gain.gain.exponentialRampToValueAtTime(0.0001, now + duration);

  osc.connect(gain);
  gain.connect(musicGain);

  osc.onended = () => {
    try {
      osc.disconnect();
      vibrato.disconnect();
      vibratoGain.disconnect();
      gain.disconnect();
    } catch {}
  };

  osc.start(now);
  vibrato.start(now);
  osc.stop(now + duration);
  vibrato.stop(now + duration);
};

// Play a plucked acoustic Mandole / Oud note
const playMandolePluck = (freq: number, duration: number, vol = 0.09) => {
  const ctx = getAudioContext();
  if (!ctx || !musicGain || isMuted || !isMusicEnabled || isRecordingActive) return;

  const now = ctx.currentTime;
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  const filter = ctx.createBiquadFilter();

  osc.type = 'triangle';
  osc.frequency.setValueAtTime(freq, now);

  filter.type = 'lowpass';
  filter.frequency.setValueAtTime(freq * 3.5, now);
  filter.frequency.exponentialRampToValueAtTime(freq * 1.2, now + duration);

  gain.gain.setValueAtTime(vol, now);
  gain.gain.exponentialRampToValueAtTime(0.0001, now + duration);

  osc.connect(filter);
  filter.connect(gain);
  gain.connect(musicGain);

  osc.onended = () => {
    try {
      osc.disconnect();
      filter.disconnect();
      gain.disconnect();
    } catch {}
  };

  osc.start(now);
  osc.stop(now + duration);
};

// Play a gentle Bendir / Darbuka percussion note
const playBendirBeat = (isDum: boolean, vol = 0.07) => {
  const ctx = getAudioContext();
  if (!ctx || !musicGain || isMuted || !isMusicEnabled || isRecordingActive) return;

  const now = ctx.currentTime;
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();

  osc.onended = () => {
    try {
      osc.disconnect();
      gain.disconnect();
    } catch {}
  };

  if (isDum) {
    osc.type = 'sine';
    osc.frequency.setValueAtTime(120, now);
    osc.frequency.exponentialRampToValueAtTime(45, now + 0.16);
    gain.gain.setValueAtTime(vol * 1.2, now);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.22);
    osc.connect(gain);
    gain.connect(musicGain);
    osc.start(now);
    osc.stop(now + 0.22);
  } else {
    osc.type = 'triangle';
    osc.frequency.setValueAtTime(320, now);
    osc.frequency.exponentialRampToValueAtTime(160, now + 0.08);
    gain.gain.setValueAtTime(vol * 0.8, now);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.1);
    osc.connect(gain);
    gain.connect(musicGain);
    osc.start(now);
    osc.stop(now + 0.1);
  }
};

// Sequencers
const stopMusicSequence = () => {
  if (musicIntervalId !== null) {
    clearInterval(musicIntervalId);
    musicIntervalId = null;
  }
};

// 1. PROLOGUE: Mystical, Ancient Flute & Meditative Berber Drone
const startPrologueMusic = () => {
  const melodyNotes = [
    { freq: NOTES.D4, dur: 1.4 },
    { freq: NOTES.Eb4, dur: 0.8 },
    { freq: NOTES.F4, dur: 1.2 },
    { freq: NOTES.G4, dur: 1.6 },
    { freq: NOTES.F4, dur: 0.8 },
    { freq: NOTES.Eb4, dur: 1.0 },
    { freq: NOTES.D4, dur: 2.2 },
    { freq: 0, dur: 1.0 },
    { freq: NOTES.A4, dur: 1.4 },
    { freq: NOTES.Bb4, dur: 0.9 },
    { freq: NOTES.A4, dur: 1.1 },
    { freq: NOTES.G4, dur: 1.4 },
    { freq: NOTES.F4, dur: 1.0 },
    { freq: NOTES.Eb4, dur: 1.2 },
    { freq: NOTES.D4, dur: 2.5 },
    { freq: 0, dur: 1.5 },
  ];

  let step = 0;
  musicIntervalId = window.setInterval(() => {
    if (!isMusicEnabled || isMuted) return;

    const current = melodyNotes[step % melodyNotes.length];
    if (current && current.freq > 0) {
      playGasbaNote(current.freq, current.dur, 0.07);
      if (step % 4 === 0) {
        playMandolePluck(NOTES.D3, 2.0, 0.05);
      }
    }
    step++;
  }, 950);
};

// 2. WORLD MAP: Warm Acoustic Journey, Folk Mandole & Soft Bendir
const mandoleMelody = [
  NOTES.D4, NOTES.F4, NOTES.A4, NOTES.G4,
  NOTES.F4, NOTES.E4, NOTES.D4, NOTES.A3,
  NOTES.D4, NOTES.E4, NOTES.F4, NOTES.G4,
  NOTES.A4, NOTES.C5, NOTES.A4, NOTES.D4,
];

const startWorldMapMusic = () => {
  let step = 0;
  musicIntervalId = window.setInterval(() => {
    if (!isMusicEnabled || isMuted) return;

    const note = mandoleMelody[step % mandoleMelody.length];
    playMandolePluck(note, 0.45, 0.08);

    if (step % 4 === 0) {
      playMandolePluck(NOTES.D3, 0.9, 0.07);
      playBendirBeat(true, 0.06);
    } else if (step % 2 === 0) {
      playBendirBeat(false, 0.04);
    }

    if (step % 16 === 8) {
      playGasbaNote(NOTES.A4, 1.0, 0.05);
    } else if (step % 16 === 12) {
      playGasbaNote(NOTES.D5, 1.2, 0.05);
    }

    step++;
  }, 380);
};

// 3. IN GAME: Calming, Focused Mountain Exploration
const inGameSequence = [
  NOTES.A3, NOTES.C4, NOTES.D4, NOTES.E4,
  NOTES.G4, NOTES.E4, NOTES.D4, NOTES.C4,
  NOTES.D4, NOTES.E4, NOTES.G4, NOTES.A4,
  NOTES.G4, NOTES.E4, NOTES.D4, NOTES.A3,
];

const startInGameMusic = () => {
  let step = 0;
  musicIntervalId = window.setInterval(() => {
    if (!isMusicEnabled || isMuted) return;

    const note = inGameSequence[step % inGameSequence.length];
    playMandolePluck(note, 0.55, 0.065);

    if (step % 4 === 0) {
      playBendirBeat(true, 0.05);
    } else if (step % 4 === 2) {
      playBendirBeat(false, 0.035);
    }

    step++;
  }, 420);
};

// 4. BOSS BATTLE: Dramatic, Rhythmic, Energetic Berber Battle Pulse
const bossBass = [NOTES.D3, NOTES.D3, NOTES.F3, NOTES.G3, NOTES.D3, NOTES.D3, NOTES.Eb3, NOTES.D3];
const bossLeads = [NOTES.D4, NOTES.Eb4, NOTES.F4, NOTES.G4, NOTES.A4, NOTES.Bb4, NOTES.A4, NOTES.G4];

const startBossMusic = () => {
  let step = 0;
  musicIntervalId = window.setInterval(() => {
    if (!isMusicEnabled || isMuted) return;

    if (step % 2 === 0) {
      playBendirBeat(true, 0.09);
    } else {
      playBendirBeat(false, 0.07);
    }

    const bassNote = bossBass[step % bossBass.length];
    playMandolePluck(bassNote, 0.28, 0.08);

    if (step % 4 === 0) {
      const leadNote = bossLeads[(step / 2) % bossLeads.length];
      playGasbaNote(leadNote, 0.6, 0.08);
    }

    step++;
  }, 260);
};

// 5. VICTORY: Joyful Festival Melody & Chimes
const victoryMelody = [
  NOTES.D4, NOTES.F4, NOTES.A4, NOTES.D5,
  NOTES.C5, NOTES.A4, NOTES.G4, NOTES.A4,
  NOTES.D5, NOTES.C5, NOTES.A4, NOTES.G4,
  NOTES.F4, NOTES.E4, NOTES.D4, NOTES.D4,
];

const startVictoryMusic = () => {
  let step = 0;
  musicIntervalId = window.setInterval(() => {
    if (!isMusicEnabled || isMuted) return;

    const note = victoryMelody[step % victoryMelody.length];
    playMandolePluck(note, 0.4, 0.085);

    if (step % 4 === 0) {
      playGasbaNote(note * 1.5, 0.5, 0.06);
      playBendirBeat(true, 0.06);
    } else if (step % 2 === 0) {
      playBendirBeat(false, 0.045);
    }

    step++;
  }, 320);
};

// Main BGM playback function
export const playBGM = (track: BGMTrack): void => {
  if (currentTrack === track && musicIntervalId !== null) {
    return;
  }

  currentTrack = track;
  stopMusicSequence();

  if (isRecordingActive) {
    notifyListeners();
    return;
  }

  startAmbienceForTrack(track);

  if (!isMusicEnabled || track === 'none') {
    notifyListeners();
    return;
  }

  switch (track) {
    case 'prologue':
      startPrologueMusic();
      break;
    case 'world_map':
      startWorldMapMusic();
      break;
    case 'in_game':
      startInGameMusic();
      break;
    case 'boss':
      startBossMusic();
      break;
    case 'victory':
      startVictoryMusic();
      break;
  }

  notifyListeners();
};

export const stopBGM = (): void => {
  currentTrack = 'none';
  stopMusicSequence();
  stopAmbience();
  notifyListeners();
};

// =========================================================================
// HAPTIC & TACTILE FEEDBACK ENGINE (iOS/Android & Desktop Clavier Taps)
// =========================================================================
export type HapticStyle = 'light' | 'medium' | 'heavy' | 'selection' | 'rigid' | 'soft';

// Ultra-lightweight discrete mechanical tap synthesis (iOS keyboard style)
export const playKeyboardTap = (): void => {
  try {
    const ctx = getAudioContext();
    if (!ctx || isMuted) return;
    if (ctx.state === 'suspended') {
      ctx.resume().catch(() => {});
    }

    const now = ctx.currentTime;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    
    // Crisp, subtle wooden/mechanical click
    osc.type = 'triangle';
    osc.frequency.setValueAtTime(320, now);
    osc.frequency.exponentialRampToValueAtTime(80, now + 0.025);
    
    gain.gain.setValueAtTime(0.08, now);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.025);

    osc.connect(gain);
    if (sfxGain) {
      gain.connect(sfxGain);
    } else {
      gain.connect(ctx.destination);
    }

    osc.start(now);
    osc.stop(now + 0.025);
  } catch {}
};

export const triggerHaptic = (style: HapticStyle = 'light'): void => {
  // 1. Play subtle iOS tactile tap sound on all devices (iPhone, Mac, PC, Android)
  playKeyboardTap();

  // 2. Trigger physical vibration on supported mobile hardware (Android Chrome/Firefox)
  try {
    if (typeof window !== 'undefined' && typeof navigator !== 'undefined' && 'vibrate' in navigator) {
      if (style === 'light' || style === 'selection') {
        navigator.vibrate(28); // 28ms: optimal pulse for Android haptic actuator
      } else if (style === 'medium' || style === 'rigid') {
        navigator.vibrate(40);
      } else if (style === 'heavy') {
        navigator.vibrate([30, 40, 30]);
      } else if (style === 'soft') {
        navigator.vibrate(20);
      }
    }
  } catch {}
};

